// Slovak Entity-Specific Payroll Calculations
// Handles different calculation methods for various Slovak business entity types

import { supabase } from '@/integrations/supabase/client';

export type SlovakEntityType = 'zivnost' | 'sro' | 'as' | 'komanditna' | 'vos' | 'other';

export interface EntitySpecificRates {
  entity_type: string;
  applicable_tax_rate: number;
  health_insurance_rate: number;
  social_insurance_rate: number;
  requires_social_insurance: boolean;
  minimum_health_payment: number;
  minimum_social_payment: number;
}

export interface ZivnostCalculationResult {
  income_tax: number;
  health_insurance: number;
  social_insurance: number;
  net_income: number;
  current_tax_bracket: '15%' | '19%' | '25%';
  social_insurance_required: boolean;
  monthly_minimums: {
    health: number;
    social: number;
  };
}

export interface CorporateCalculationResult {
  gross_salary: number;
  employee_taxes: number;
  employee_social_insurance: number;
  employee_health_insurance: number;
  net_salary: number;
  employer_social_costs: number;
  total_employer_cost: number;
}

/**
 * Get entity-specific rates from database function
 */
export const getEntitySpecificRates = async (
  businessId: string, 
  income: number = 0
): Promise<EntitySpecificRates | null> => {
  try {
    const { data, error } = await supabase
      .rpc('get_slovak_entity_rates', {
        p_business_id: businessId,
        p_income: income
      })
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting entity rates:', error);
    return null;
  }
};

/**
 * Calculate živnosť (individual entrepreneur) payroll
 */
export const calculateZivnostPayroll = async (
  businessId: string,
  grossIncome: number,
  currentYearIncome: number = 0
): Promise<ZivnostCalculationResult> => {
  const rates = await getEntitySpecificRates(businessId, currentYearIncome);
  
  if (!rates) {
    throw new Error('Unable to load entity-specific rates');
  }

  // Income tax calculation with progressive brackets
  let income_tax = 0;
  let current_tax_bracket: '15%' | '19%' | '25%' = '15%';

  if (currentYearIncome <= 49790) {
    income_tax = grossIncome * 0.15;
    current_tax_bracket = '15%';
  } else if (currentYearIncome <= 41445.46) {
    // This is annual taxable income, not total income
    income_tax = grossIncome * 0.19;
    current_tax_bracket = '19%';
  } else {
    income_tax = grossIncome * 0.25;
    current_tax_bracket = '25%';
  }

  // Health insurance (always required)
  const health_insurance = Math.max(
    grossIncome * rates.health_insurance_rate,
    rates.minimum_health_payment
  );

  // Social insurance (may be delayed)
  let social_insurance = 0;
  const social_insurance_required = rates.requires_social_insurance;
  
  if (social_insurance_required) {
    social_insurance = Math.max(
      grossIncome * rates.social_insurance_rate,
      rates.minimum_social_payment
    );
  }

  const net_income = grossIncome - income_tax - health_insurance - social_insurance;

  return {
    income_tax,
    health_insurance,
    social_insurance,
    net_income,
    current_tax_bracket,
    social_insurance_required,
    monthly_minimums: {
      health: rates.minimum_health_payment,
      social: rates.minimum_social_payment
    }
  };
};

/**
 * Calculate corporate entity (s.r.o., a.s.) employee payroll
 */
export const calculateCorporateEmployeePayroll = async (
  businessId: string,
  grossSalary: number
): Promise<CorporateCalculationResult> => {
  const rates = await getEntitySpecificRates(businessId, grossSalary);
  
  if (!rates) {
    throw new Error('Unable to load entity-specific rates');
  }

  // Employee deductions
  const employee_social_insurance = grossSalary * rates.social_insurance_rate;
  const employee_health_insurance = grossSalary * rates.health_insurance_rate;
  
  // Taxable income after social insurance
  const taxable_income = grossSalary - employee_social_insurance - employee_health_insurance;
  
  // Income tax (progressive for employees)
  let employee_taxes = 0;
  const monthly_allowance = 4335; // €4,335 annual allowance / 12
  const taxable_after_allowance = Math.max(0, taxable_income - monthly_allowance);
  
  // Progressive tax calculation
  const monthly_tier1_limit = 49790 / 12; // €4,149.17
  
  if (taxable_after_allowance <= monthly_tier1_limit) {
    employee_taxes = taxable_after_allowance * 0.19;
  } else {
    employee_taxes = monthly_tier1_limit * 0.19 + 
                    (taxable_after_allowance - monthly_tier1_limit) * 0.25;
  }

  const net_salary = grossSalary - employee_social_insurance - employee_health_insurance - employee_taxes;

  // Employer costs (different rates for employers)
  const employer_social_costs = grossSalary * (
    0.14 +    // Pension insurance
    0.03 +    // Disability insurance
    0.045 +   // Sickness insurance
    0.01 +    // Unemployment insurance
    0.008 +   // Accident insurance
    0.0025 +  // Guarantee fund
    0.004     // Solidarity contribution
  ); // Total: 25.25%
  
  const employer_health_costs = grossSalary * 0.10; // 10% employer health insurance
  
  const total_employer_cost = grossSalary + employer_social_costs + employer_health_costs;

  return {
    gross_salary: grossSalary,
    employee_taxes,
    employee_social_insurance,
    employee_health_insurance,
    net_salary,
    employer_social_costs: employer_social_costs + employer_health_costs,
    total_employer_cost
  };
};

/**
 * Get tax bracket information for živnosť
 */
export const getZivnostTaxBrackets = (currentIncome: number = 0) => {
  return [
    {
      bracket: '15%',
      range: '€0 - €49,790',
      rate: 0.15,
      isCurrentBracket: currentIncome <= 49790,
      description: 'First tax bracket - most favorable rate'
    },
    {
      bracket: '19%',
      range: '€49,790+ (taxable income up to €41,445.46)',
      rate: 0.19,
      isCurrentBracket: currentIncome > 49790 && currentIncome <= 41445.46,
      description: 'Second tax bracket on taxable income'
    },
    {
      bracket: '25%',
      range: 'Taxable income above €41,445.46',
      rate: 0.25,
      isCurrentBracket: currentIncome > 41445.46,
      description: 'Highest tax bracket'
    }
  ];
};

/**
 * Get corporate tax rates for s.r.o./a.s.
 */
export const getCorporateTaxRates = (revenue: number = 0) => {
  return [
    {
      bracket: '10%',
      range: '€0 - €100,000',
      rate: 0.10,
      isCurrentBracket: revenue <= 100000,
      description: 'Small company rate'
    },
    {
      bracket: '21%',
      range: '€100,001 - €5,000,000', 
      rate: 0.21,
      isCurrentBracket: revenue > 100000 && revenue <= 5000000,
      description: 'Standard corporate rate'
    },
    {
      bracket: '24%',
      range: 'Above €5,000,000',
      rate: 0.24,
      isCurrentBracket: revenue > 5000000,
      description: 'Large company rate'
    }
  ];
};

/**
 * Calculate minimum payments for živnosť
 */
export const calculateZivnostMinimumPayments = async (businessId: string) => {
  const rates = await getEntitySpecificRates(businessId, 0);
  
  if (!rates) {
    return {
      health_insurance: 91.28,
      social_insurance: 216.13,
      total: 307.41
    };
  }

  return {
    health_insurance: rates.minimum_health_payment,
    social_insurance: rates.minimum_social_payment,
    total: rates.minimum_health_payment + rates.minimum_social_payment
  };
};

/**
 * Check if social insurance is required for živnosť
 */
export const checkZivnostSocialInsuranceRequirement = async (
  businessId: string, 
  currentYearIncome: number
): Promise<{
  required: boolean;
  threshold: number;
  remaining: number;
  message: string;
}> => {
  const rates = await getEntitySpecificRates(businessId, currentYearIncome);
  
  if (!rates) {
    return {
      required: false,
      threshold: 6078,
      remaining: 6078,
      message: 'Unable to determine requirement'
    };
  }

  const threshold = 6078; // Current threshold
  const required = rates.requires_social_insurance;
  const remaining = Math.max(0, threshold - currentYearIncome);

  let message = '';
  if (required) {
    message = 'Social insurance contributions are required';
  } else if (remaining > 0) {
    message = `Social insurance starts after earning additional €${remaining.toFixed(2)}`;
  } else {
    message = 'Social insurance contributions start next year';
  }

  return {
    required,
    threshold,
    remaining,
    message
  };
};

/**
 * Entity-specific payroll calculation dispatcher
 */
export const calculateEntitySpecificPayroll = async (
  businessId: string,
  entityType: SlovakEntityType,
  grossAmount: number,
  additionalData?: {
    currentYearIncome?: number;
    isEmployeeSalary?: boolean;
  }
) => {
  switch (entityType) {
    case 'zivnost':
      return await calculateZivnostPayroll(
        businessId, 
        grossAmount, 
        additionalData?.currentYearIncome || 0
      );
      
    case 'sro':
    case 'as':
      if (additionalData?.isEmployeeSalary) {
        return await calculateCorporateEmployeePayroll(businessId, grossAmount);
      } else {
        // Corporate profit calculation would go here
        throw new Error('Corporate profit calculation not implemented yet');
      }
      
    case 'komanditna':
    case 'vos':
      // Partnership calculations - similar to živnosť but with partner splits
      return await calculateZivnostPayroll(
        businessId, 
        grossAmount, 
        additionalData?.currentYearIncome || 0
      );
      
    default:
      throw new Error(`Unsupported entity type: ${entityType}`);
  }
};

/**
 * Format currency for Slovak locale
 */
export const formatSlovakCurrency = (amount: number) => {
  return new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(amount);
};

/**
 * Format percentage for Slovak locale
 */
export const formatSlovakPercentage = (rate: number) => {
  return new Intl.NumberFormat('sk-SK', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 2
  }).format(rate);
};